import { useState, useEffect } from 'react';
import { X, Plus, Building2 } from 'lucide-react';
import { generateUuid } from '../../utils/uuid';
import { normalizePhone } from '../../utils/phone';

const EMPRESA_NOVA = '__nova_empresa__';

export default function ContactFormModal({
  contact,
  companies,
  tags,
  channels,
  contacts,
  onSave,
  onClose,
  checkDuplicate,
  onAddCompany,
}) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    email: '',
    empresa_id: null,
    tags: [],
    observacoes_internas: '',
    canal_origem: 'whatsapp',
    status: 'ativo',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    nome_fantasia: '',
    razao_social: '',
    cnpj: '',
  });

  useEffect(() => {
    setSaveError(null);
    setShowNewCompany(false);
    setNewCompanyForm({ nome_fantasia: '', razao_social: '', cnpj: '' });
    if (contact) {
      setForm({
        nome: contact.nome || '',
        telefone: contact.telefone || '',
        email: contact.email || '',
        empresa_id: contact.empresa_id || null,
        tags: contact.tags || [],
        observacoes_internas: contact.observacoes_internas || '',
        canal_origem: contact.canal_origem || 'whatsapp',
        status: contact.status || 'ativo',
      });
    } else {
      setForm({
        nome: '',
        telefone: '',
        email: '',
        empresa_id: null,
        tags: [],
        observacoes_internas: '',
        canal_origem: 'whatsapp',
        status: 'ativo',
      });
    }
  }, [contact]);

  const telefoneReadOnly = isEdit && form.canal_origem === 'whatsapp';

  const validate = () => {
    const e = {};
    if (!form.nome?.trim()) {
      e.nome = 'Informe o nome do contato';
    }
    if (!form.telefone?.trim() && !form.email?.trim()) {
      e.telefone = 'Informe telefone ou e-mail';
      e.email = 'Informe telefone ou e-mail';
    }
    if (form.telefone?.trim() && checkDuplicate('telefone', form.telefone, contact?.id)) {
      e.telefone = 'Telefone já cadastrado';
    }
    if (form.email?.trim() && checkDuplicate('email', form.email, contact?.id)) {
      e.email = 'E-mail já cadastrado';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...form,
      telefone: form.telefone?.trim() ? normalizePhone(form.telefone) : '',
      email: form.email?.trim() || '',
      empresa_id: form.empresa_id || null,
      tags: form.tags,
      observacoes_internas: form.observacoes_internas || '',
    };

    const dataToSave = isEdit
      ? { ...contact, ...payload }
      : {
          id: generateUuid(),
          ...payload,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: null,
          historico_edicoes: [],
        };

    setSaveError(null);
    setSaving(true);
    try {
      await onSave?.(dataToSave, !isEdit);
    } catch (err) {
      setSaveError(err?.message || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tagId)
        ? f.tags.filter((t) => t !== tagId)
        : [...f.tags, tagId],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-appSurface rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-appSurfaceBorder">
          <h2 className="text-xl font-bold text-appText">
            {isEdit ? 'Editar contato' : 'Novo contato'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Nome *</label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => { setForm((f) => ({ ...f, nome: e.target.value })); setSaveError(null); }}
              placeholder="Nome do contato"
              className={`w-full px-4 py-3 rounded-xl border text-appText focus:ring-2 focus:ring-appPrimary outline-none ${errors.nome ? 'border-rose-500' : 'border-appSurfaceBorder'}`}
            />
            {errors.nome && <p className="text-xs text-rose-600 mt-1">{errors.nome}</p>}
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              Telefone {form.telefone || form.email ? '' : '(obrigatório se não informar e-mail)'}
            </label>
            <input
              type="tel"
              value={form.telefone}
              onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
              readOnly={telefoneReadOnly}
              placeholder="+55 11 99999-9999"
              className={`w-full px-4 py-3 rounded-xl border text-appText focus:ring-2 focus:ring-appPrimary outline-none ${
                telefoneReadOnly ? 'bg-appSurfaceBorder/30 border-appSurfaceBorder' : 'border-appSurfaceBorder'
              } ${errors.telefone ? 'border-rose-500' : ''}`}
            />
            {telefoneReadOnly && (
              <p className="text-xs text-appTextMuted mt-1">
                Telefone de contatos do WhatsApp não pode ser alterado
              </p>
            )}
            {errors.telefone && <p className="text-xs text-rose-600 mt-1">{errors.telefone}</p>}
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              E-mail {form.telefone || form.email ? '' : '(obrigatório se não informar telefone)'}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="email@exemplo.com"
              className={`w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none ${errors.email ? 'border-rose-500' : ''}`}
            />
            {errors.email && <p className="text-xs text-rose-600 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Empresa</label>
            {!showNewCompany ? (
              <>
                <select
                  value={form.empresa_id || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === EMPRESA_NOVA) {
                      setShowNewCompany(true);
                      setForm((f) => ({ ...f, empresa_id: null }));
                    } else {
                      setForm((f) => ({ ...f, empresa_id: v || null }));
                    }
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
                >
                  <option value="">Sem empresa</option>
                  {companies.filter((c) => c.status === 'ativo').map((c) => (
                    <option key={c.id} value={c.id}>{c.nome_fantasia}</option>
                  ))}
                  {onAddCompany && (
                    <option value={EMPRESA_NOVA}>+ Cadastrar nova empresa</option>
                  )}
                </select>
              </>
            ) : (
              <div className="p-4 bg-appSurfaceBorder/10 rounded-2xl border border-appSurfaceBorder space-y-3">
                <div className="flex items-center gap-2 text-appText font-bold text-sm">
                  <Building2 size={18} className="text-appPrimary" />
                  Nova empresa
                </div>
                <div>
                  <label className="text-[10px] font-bold text-appTextMuted uppercase block mb-1">Nome fantasia</label>
                  <input
                    type="text"
                    value={newCompanyForm.nome_fantasia}
                    onChange={(e) => setNewCompanyForm((f) => ({ ...f, nome_fantasia: e.target.value }))}
                    placeholder="Ex: Tech Solutions"
                    className="w-full px-3 py-2 rounded-xl border border-appSurfaceBorder text-appText text-sm focus:ring-2 focus:ring-appPrimary outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-appTextMuted uppercase block mb-1">Razão social (opcional)</label>
                  <input
                    type="text"
                    value={newCompanyForm.razao_social}
                    onChange={(e) => setNewCompanyForm((f) => ({ ...f, razao_social: e.target.value }))}
                    placeholder="Ex: Tech Solutions Ltda"
                    className="w-full px-3 py-2 rounded-xl border border-appSurfaceBorder text-appText text-sm focus:ring-2 focus:ring-appPrimary outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-appTextMuted uppercase block mb-1">CNPJ (opcional)</label>
                  <input
                    type="text"
                    value={newCompanyForm.cnpj}
                    onChange={(e) => setNewCompanyForm((f) => ({ ...f, cnpj: e.target.value }))}
                    placeholder="00.000.000/0001-00"
                    className="w-full px-3 py-2 rounded-xl border border-appSurfaceBorder text-appText text-sm focus:ring-2 focus:ring-appPrimary outline-none"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={async () => {
                      const nome = newCompanyForm.nome_fantasia?.trim();
                      if (!nome || !onAddCompany) return;
                      const now = new Date().toISOString();
                      const id = `cmp-${Date.now()}`;
                      const newCompany = {
                        id,
                        nome_fantasia: nome,
                        razao_social: newCompanyForm.razao_social?.trim() || null,
                        cnpj: newCompanyForm.cnpj?.trim() || null,
                        tags: [],
                        observacoes_internas: '',
                        status: 'ativo',
                        created_at: now,
                        updated_at: now,
                      };
                      const created = await onAddCompany(newCompany);
                      const nextId = created?.id || id;
                      setForm((f) => ({ ...f, empresa_id: nextId }));
                      setShowNewCompany(false);
                      setNewCompanyForm({ nome_fantasia: '', razao_social: '', cnpj: '' });
                    }}
                    disabled={!newCompanyForm.nome_fantasia?.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 bg-appPrimary text-white rounded-xl font-bold text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={14} />
                    Criar e vincular
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCompany(false);
                      setNewCompanyForm({ nome_fantasia: '', razao_social: '', cnpj: '' });
                    }}
                    className="px-3 py-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-xs"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isEdit && (
            <div>
              <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Canal de origem</label>
              <select
                value={form.canal_origem}
                onChange={(e) => setForm((f) => ({ ...f, canal_origem: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
              >
                {channels.map((ch) => (
                  <option key={ch.id} value={ch.id}>{ch.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Tags</label>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    form.tags.includes(t.id)
                      ? 'bg-appPrimary text-white border-appPrimary'
                      : 'border-appSurfaceBorder text-appText hover:bg-appSurfaceBorder/30'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">Observações internas</label>
            <textarea
              value={form.observacoes_internas}
              onChange={(e) => setForm((f) => ({ ...f, observacoes_internas: e.target.value }))}
              placeholder="Notas internas (não visíveis ao cliente)"
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none resize-none"
            />
          </div>

          {saveError && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm">
              {saveError}
            </div>
          )}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Criar contato')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-xl font-bold text-sm"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
